import { useEffect, useState } from "react";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Network, MapPin, Building2, Clock, Info, Pencil, RefreshCw, Loader2, User } from "lucide-react";

const km = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("it-IT", { maximumFractionDigits: 1 })} km`);
const min = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("it-IT", { maximumFractionDigits: 0 })} min`);

const METHOD_LABELS = {
  average_of_3_partners: "Media dei 3 installatori più vicini",
  average_of_2_partners: "Media di 2 installatori",
  single_partner: "1 solo installatore disponibile",
  torri_headquarters_fallback: "Fallback: distanza sede TORRI",
};

function Markers({ distances }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google) return;
    const g = window.google;
    const markers = [];
    const bounds = new g.maps.LatLngBounds();
    const add = (lat, lon, label, color) => {
      if (lat === null || lat === undefined || lon === null || lon === undefined) return;
      const m = new g.maps.Marker({
        position: { lat, lng: lon }, map,
        label: { text: label, color: "#0b0b0b", fontSize: "11px", fontWeight: "800" },
        icon: { path: g.maps.SymbolPath.CIRCLE, scale: 11, fillColor: color, fillOpacity: 1, strokeColor: "#0b0b0b", strokeWeight: 1.5 },
      });
      markers.push(m);
      bounds.extend({ lat, lng: lon });
    };
    add(distances.site_lat, distances.site_lon, "C", "#FDD003");
    add(distances.hq_lat, distances.hq_lon, "T", "#60A5FA");
    (distances.partners || []).forEach((p, i) => add(p.lat, p.lon, String(i + 1), "#34D399"));
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, 60);
      const l = g.maps.event.addListenerOnce(map, "idle", () => { if (map.getZoom() > 13) map.setZoom(13); });
      return () => { markers.forEach((m) => m.setMap(null)); g.maps.event.removeListener(l); };
    }
    return () => markers.forEach((m) => m.setMap(null));
  }, [map, distances]);
  return null;
}

const parseAddr = (s) => {
  s = (s || "").trim();
  const o = { via: "", cap: "", comune: "", provincia: "" };
  if (!s) return o;
  const pm = s.match(/\(([A-Za-z]{2})\)\s*$/);
  if (pm) { o.provincia = pm[1].toUpperCase(); s = s.slice(0, pm.index).trim().replace(/,\s*$/, ""); }
  const cm = s.match(/\b(\d{5})\b/);
  if (cm) o.cap = cm[1];
  if (s.includes(",")) { const p = s.split(",").map((x) => x.trim()).filter(Boolean); o.via = p[0] || ""; if (p.length > 1) o.comune = (p[p.length - 1] || "").replace(/\b\d{5}\b/, "").trim(); }
  else o.via = s.replace(/\b\d{5}\b/, "").trim();
  return o;
};

export default function InstallerDistancesBox({ distances, lead, onRecalc, canEdit = true }) {
  const seed = () => ((lead && (lead.via || lead.comune))
    ? { via: lead.via || "", comune: lead.comune || "", cap: lead.cap || "", provincia: lead.provincia || "" }
    : parseAddr(distances?.site_label));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addr, setAddr] = useState(seed);
  const doRecalc = async () => {
    setSaving(true);
    try {
      if (lead?.id) await api.put(`/leads/${lead.id}`, { via: addr.via, comune: addr.comune, cap: addr.cap, provincia: addr.provincia });
      if (onRecalc) await onRecalc();
      setEditing(false);
      toast.success("Anagrafica aggiornata e distanze ricalcolate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel ricalcolo");
    } finally { setSaving(false); }
  };
  if (!distances) return null;
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const partners = distances.partners || [];
  const center = { lat: distances.site_lat || distances.hq_lat || 45.35, lng: distances.site_lon || distances.hq_lon || 10.14 };

  const Stat = ({ icon: Icon, label, value, sub, testid }) => (
    <div className="rounded border border-border bg-secondary/20 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Icon className="h-3 w-3" /> {label}</div>
      <div className="font-heading font-bold text-lg mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );

  return (
    <div className="rounded border border-border bg-card p-4 mb-4" data-testid="installer-distances-box">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><Network className="h-4 w-4 text-primary" strokeWidth={1.5} /> Distanze e rete installatori <span className="text-[10px] font-normal text-muted-foreground uppercase tracking-wide">(riservato)</span></h3>
        <Badge variant="outline" className={`font-normal text-[11px] ${distances.fallback ? "border-amber-500/40 text-amber-500" : "border-primary/40 text-primary"}`} data-testid="distances-method">{METHOD_LABELS[distances.calculation_method] || distances.calculation_method}</Badge>
      </div>

      {lead && onRecalc && (
        <div className="rounded border border-border bg-secondary/20 p-3 mb-3" data-testid="distances-anagrafica">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><User className="h-3 w-3" /> Anagrafica considerata (cantiere)</div>
              <div className="text-sm font-medium truncate" data-testid="distances-site-label">{distances.site_label || "—"}</div>
            </div>
            {canEdit && !editing && (
              <Button size="sm" variant="outline" onClick={() => { setAddr(seed()); setEditing(true); }} data-testid="distances-edit-btn"><Pencil className="h-3.5 w-3.5 mr-1.5" /> Modifica</Button>
            )}
          </div>
          {editing && (
            <div className="mt-3 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
                <Input className="sm:col-span-4" value={addr.via} onChange={(e) => setAddr((a) => ({ ...a, via: e.target.value }))} placeholder="Via e numero civico" data-testid="distances-edit-via" />
                <Input className="sm:col-span-2" value={addr.cap} onChange={(e) => setAddr((a) => ({ ...a, cap: e.target.value }))} placeholder="CAP" data-testid="distances-edit-cap" />
                <Input className="sm:col-span-4" value={addr.comune} onChange={(e) => setAddr((a) => ({ ...a, comune: e.target.value }))} placeholder="Comune" data-testid="distances-edit-comune" />
                <Input className="sm:col-span-2" value={addr.provincia} onChange={(e) => setAddr((a) => ({ ...a, provincia: e.target.value.toUpperCase() }))} placeholder="Prov." maxLength={2} data-testid="distances-edit-provincia" />
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={doRecalc} disabled={saving} data-testid="distances-recalc-btn">{saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Salva e ricalcola</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving} data-testid="distances-cancel-btn">Annulla</Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <Stat icon={Building2} label="Sede TORRI → cantiere" value={km(distances.hq_distance_km)} sub={min(distances.hq_time_min)} testid="distances-hq" />
        <Stat icon={Network} label={`Media ${distances.num_partners_used || 0} partner`} value={km(distances.partner_average_distance_km)} sub={distances.partner_average_time_min != null ? `${min(distances.partner_average_time_min)} medi` : "—"} testid="distances-avg" />
        <Stat icon={MapPin} label="Partner più vicino" value={km(distances.nearest_partner_km)} testid="distances-nearest" />
        <Stat icon={Clock} label="Km usati per la stima" value={km(distances.cost_distance_km)} sub={distances.coeff_prudenza !== 1 ? `× ${distances.coeff_prudenza} prudenza` : "coeff. 1.0"} testid="distances-cost-km" />
      </div>

      {partners.length > 0 && (
        <div className="rounded border border-border divide-y divide-border mb-3" data-testid="distances-partners">
          {partners.map((p, i) => (
            <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`distances-partner-${i}`}>
              <div className="min-w-0">
                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-success/20 text-success text-[11px] font-bold mr-2">{i + 1}</span>
                <span className="font-medium">{p.ragione_sociale}</span>
                <span className="text-muted-foreground"> · {p.comune}{p.provincia ? ` (${p.provincia})` : ""}</span>
              </div>
              <div className="text-xs text-muted-foreground whitespace-nowrap">{km(p.distanza_km)} · {min(p.tempo_min)}</div>
            </div>
          ))}
        </div>
      )}

      {apiKey && (distances.site_lat || partners.length > 0) && (
        <div className="rounded overflow-hidden border border-border mb-3" style={{ height: 260 }} data-testid="distances-map">
          <APIProvider apiKey={apiKey}>
            <Map defaultCenter={center} defaultZoom={9} gestureHandling="cooperative" disableDefaultUI={false} mapTypeId="roadmap" style={{ width: "100%", height: "100%" }}>
              <Markers distances={distances} />
            </Map>
          </APIProvider>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground flex items-start gap-1.5" data-testid="distances-disclaimer">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>Gli Installatori Partner individuati sono utilizzati esclusivamente come riferimento geografico ed economico. L'individuazione <b>non costituisce assegnazione della commessa</b>. Dati non visibili al cliente.{distances.computed_at ? ` · Aggiornato: ${new Date(distances.computed_at).toLocaleString("it-IT")}` : ""}</span>
      </p>
    </div>
  );
}
